import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { Button, Form, Grid, Input } from '@arco-design/web-react';
import type { FormInstance } from '@arco-design/web-react/es/Form/interface';
import { IconDelete, IconPlus } from '@arco-design/web-react/icon';

import { PATTERN_ENV_NAME } from '@/utils/regexp';

import type { CommonImageItemType } from '../types';
import {
  type ContainerStartupPayload,
  commaTextToStartupArray,
  getContainerStartupDefaultsFromImageItem,
  startupArrayToCommaText,
} from '../utils/containerStartup';

const { TextArea } = Input;
const Row = Grid.Row;
const Col = Grid.Col;

function fieldPath(prefix: string, key: string) {
  return `${prefix}.${key}`;
}

export type { ContainerStartupPayload };

export interface ContainerImageStartupCommandFormProps {
  form: FormInstance;
  /** 与 basic.image.url、basic.image.key 同级的前缀，如 basic.image */
  prefix: string;
  /** 当前选中的镜像项，切换时写入默认启动配置 */
  imageItem?: CommonImageItemType | null;
  /** installScript 解析为空时的兜底 */
  defaultCommand?: string[];
  defaultArgs?: string[];
  defaultEnv?: ContainerStartupPayload['env'];
}

export interface ContainerImageStartupCommandStandaloneProps {
  form?: never;
  value: ContainerStartupPayload;
  onChange: (value: ContainerStartupPayload) => void;
  imageItem?: CommonImageItemType | null;
  defaultCommand?: string[];
  defaultArgs?: string[];
  defaultEnv?: ContainerStartupPayload['env'];
}

export type ContainerImageStartupCommandProps = ContainerImageStartupCommandFormProps | ContainerImageStartupCommandStandaloneProps;

function isFormMode(props: ContainerImageStartupCommandProps): props is ContainerImageStartupCommandFormProps {
  // 表单模式以 prefix 区分；form 可能尚未挂载，不能仅用 !!form 判断
  return 'prefix' in props;
}

const emptyStartupPayload: ContainerStartupPayload = { command: [], args: [], env: [] };

/**
 * 容器镜像启动命令：启动命令、参数（逗号分隔多段）、环境变量列表。
 * 表单模式写入 prefix.command / prefix.args（字符串）与 prefix.env（{name,value}[]）；
 * 非表单模式通过 value + onChange 受控，并在镜像切换时 onChange 最新默认值。
 */
export function ContainerImageStartupCommand(props: ContainerImageStartupCommandProps) {
  const { t } = useTranslation();
  const { imageItem, defaultCommand, defaultArgs, defaultEnv } = props;
  const isForm = isFormMode(props);
  const formInst = isForm ? props.form : undefined;
  const prefixVal = isForm ? props.prefix : '';
  const standaloneOnChange = !isForm ? (props as ContainerImageStartupCommandStandaloneProps).onChange : undefined;
  const lastImageSigRef = useRef<string | undefined>(undefined);

  useEffect(() => {
    const imageKey = imageItem?.id ?? imageItem?.key;
    const installDigest = imageItem?.installScript ?? '';
    if (!imageItem || !imageKey) {
      lastImageSigRef.current = undefined;
      return;
    }
    const sig = `${imageKey}::${installDigest}`;
    if (lastImageSigRef.current === sig) return;
    lastImageSigRef.current = sig;

    const next = getContainerStartupDefaultsFromImageItem(imageItem, {
      command: defaultCommand,
      args: defaultArgs,
      env: defaultEnv,
    });

    if (isForm && formInst && prefixVal) {
      formInst.setFieldValue(fieldPath(prefixVal, 'command'), startupArrayToCommaText(next.command));
      formInst.setFieldValue(fieldPath(prefixVal, 'args'), startupArrayToCommaText(next.args));
      formInst.setFieldValue(fieldPath(prefixVal, 'env'), next.env.length ? next.env : []);
    } else if (standaloneOnChange) {
      standaloneOnChange(next);
    }
  }, [
    isForm,
    formInst,
    prefixVal,
    imageItem,
    imageItem?.id,
    imageItem?.key,
    imageItem?.installScript,
    defaultArgs,
    defaultCommand,
    defaultEnv,
    standaloneOnChange,
  ]);

  if (isForm) {
    if (!formInst) return null;
    const { prefix: pfx } = props as ContainerImageStartupCommandFormProps;
    const envField = fieldPath(pfx, 'env');
    return (
      <div style={{ marginBottom: 24 }}>
        <div style={{ marginBottom: 8, fontWeight: 500 }}>{t('components:startup.command')}</div>
        <Form.Item
          label={t('cluster:command')}
          field={fieldPath(pfx, 'command')}
          initialValue=""
          extra={t('components:container.startup.command.description')}
          style={{ marginBottom: 6 }}
        >
          <TextArea placeholder={t('components:please.enter.execution.command')} autoSize={{ minRows: 2, maxRows: 6 }} />
        </Form.Item>
        <Form.Item
          label={t('cluster:parameters')}
          field={fieldPath(pfx, 'args')}
          initialValue=""
          extra={t('components:container.startup.command.parameters.description')}
          style={{ marginBottom: 8 }}
        >
          <TextArea placeholder={t('components:please.enter.parameters')} autoSize={{ minRows: 2, maxRows: 6 }} />
        </Form.Item>
        <div style={{ marginBottom: 8, fontWeight: 500 }}>{t('common:environment')}</div>
        <Form.List field={envField}>
          {(fields, { add, remove }) => (
            <div>
              {fields.map((item, index) => (
                <div key={item.key} style={{ display: 'flex', alignItems: 'flex-start', marginBottom: 4 }}>
                  <Row gutter={12} style={{ flex: 1 }}>
                    <Col span={10}>
                      <Form.Item
                        field={`${item.field}.name`}
                        rules={[
                          {
                            validator(v, cb) {
                              const name = v != null ? String(v).trim() : '';
                              if (!name) {
                                cb();
                                return;
                              }
                              if (!PATTERN_ENV_NAME.test(name)) {
                                cb(
                                  t(
                                    'components:environment.variable.keys.can.only.contain.letters.numbers.underscores.hyphens.and.periods.and.cannot.start.with.a.number',
                                  ),
                                );
                                return;
                              }
                              cb();
                            },
                          },
                        ]}
                        style={{ marginBottom: 0 }}
                      >
                        <Input placeholder={t('common:key')} />
                      </Form.Item>
                    </Col>
                    <Col span={14}>
                      <Form.Item field={`${item.field}.value`} style={{ marginBottom: 0 }}>
                        <Input placeholder={t('common:value')} />
                      </Form.Item>
                    </Col>
                  </Row>
                  <Button icon={<IconDelete />} type="text" onClick={() => remove(index)} style={{ marginLeft: 8 }} />
                </div>
              ))}
              <Button type="primary" size="mini" icon={<IconPlus />} onClick={() => add({ name: '', value: '' })}>
                {t('common:add')}
              </Button>
            </div>
          )}
        </Form.List>
      </div>
    );
  }

  const { value = emptyStartupPayload, onChange } = props as ContainerImageStartupCommandStandaloneProps;

  const setPartial = (patch: Partial<ContainerStartupPayload>) => {
    onChange({
      command: patch.command ?? value.command,
      args: patch.args ?? value.args,
      env: patch.env ?? value.env,
    });
  };

  return (
    <div style={{ marginTop: 16 }}>
      <div style={{ marginBottom: 8, fontWeight: 500 }}>{t('components:startup.command')}</div>
      <div style={{ marginBottom: 4 }}>{t('cluster:command')}</div>
      <TextArea
        value={startupArrayToCommaText(value.command)}
        onChange={(s: string) => setPartial({ command: commaTextToStartupArray(s) })}
        placeholder={t('components:please.enter.execution.command')}
        autoSize={{ minRows: 2, maxRows: 6 }}
      />
      <div style={{ fontSize: 12, color: 'var(--color-text-3)', marginTop: 4 }}>
        {t('components:container.startup.command.description')}
      </div>
      <div style={{ marginTop: 12, marginBottom: 4 }}>{t('cluster:parameters')}</div>
      <TextArea
        value={startupArrayToCommaText(value.args)}
        onChange={(s: string) => setPartial({ args: commaTextToStartupArray(s) })}
        placeholder={t('components:please.enter.parameters')}
        autoSize={{ minRows: 2, maxRows: 6 }}
      />
      <div style={{ fontSize: 12, color: 'var(--color-text-3)', marginTop: 4 }}>
        {t('components:container.startup.command.parameters.description')}
      </div>
      <div style={{ marginTop: 16, marginBottom: 8, fontWeight: 500 }}>{t('common:environment')}</div>
      {value.env.map((row, index) => (
        <div key={index} style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
          <Input
            style={{ width: '38%' }}
            placeholder={t('common:key')}
            value={row.name}
            onChange={(v) => {
              const next = [...value.env];
              next[index] = { ...next[index], name: v };
              setPartial({ env: next });
            }}
          />
          <Input
            style={{ flex: 1 }}
            placeholder={t('common:value')}
            value={row.value}
            onChange={(v) => {
              const next = [...value.env];
              next[index] = { ...next[index], value: v };
              setPartial({ env: next });
            }}
          />
          <Button
            icon={<IconDelete />}
            type="text"
            onClick={() => {
              const next = value.env.filter((_, i) => i !== index);
              setPartial({ env: next.length ? next : [] });
            }}
          />
        </div>
      ))}
      <Button type="primary" size="mini" icon={<IconPlus />} onClick={() => setPartial({ env: [...value.env, { name: '', value: '' }] })}>
        {t('common:add')}
      </Button>
    </div>
  );
}
